import { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { formatCurrency } from '@/utils/currency';

type PaymentProcessingMethod = 'card' | 'cash' | 'transfer';

interface PaymentProcessingModalProps {
  isOpen: boolean;
  /**
   * #687 fail-closed: onSuccess 콜백 제거 — 이 모달은 서버 권위 신호(payment paid /
   * 3DS challenge 완주 콜백)에 접근할 수 없으므로 결제 성공을 선언할 수 없다.
   * 기존 Math.random 모의 성공(80%)이 거짓 '결제 완료' 화면의 root cause 였다.
   */
  onError: () => void;
  /**
   * 결제 메서드 — 'cash' / 'transfer' 시 카드 검증/은행 통신 단계 숨기고
   * 주문 접수 흐름만 표시 (사용자 박제: 결제 = 현금/카드, Stripe 아님).
   * 미지정 시 'card' 폴백 (하위 호환).
   */
  method?: PaymentProcessingMethod;
  /**
   * 카드 정보 — method='card' 일 때만 필수. cash/transfer 분기는 카드 박스 미렌더라
   * dummy 값을 강제 전달하지 않도록 optional (codex 1R P1 시정).
   */
  cardInfo?: {
    type: string;
    lastFour: string;
    holder: string;
  };
  amount: number;
}

const translations = {
  ko: {
    processing: '결제 처리 중...',
    pleaseWait: '잠시만 기다려주세요',
    cardVerification: '카드 정보 확인 중',
    bankCommunication: '은행과 통신 중',
    finalizing: '결제 완료 중',
    amount: '결제 금액',
    card: '결제 카드',
    method: '결제 수단',
    methodCash: '현금',
    methodTransfer: '계좌이체',
    orderRegistering: '주문 접수 중',
    orderConfirming: '매장에 전달 중',
  },
  es: {
    processing: 'Procesando pago...',
    pleaseWait: 'Por favor espere',
    cardVerification: 'Verificando información de la tarjeta',
    bankCommunication: 'Comunicándose con el banco',
    finalizing: 'Finalizando pago',
    amount: 'Monto del pago',
    card: 'Tarjeta de pago',
    method: 'Método de pago',
    methodCash: 'Efectivo',
    methodTransfer: 'Transferencia',
    orderRegistering: 'Registrando el pedido',
    orderConfirming: 'Notificando a la tienda',
  },
  en: {
    processing: 'Processing payment...',
    pleaseWait: 'Please wait',
    cardVerification: 'Verifying card information',
    bankCommunication: 'Communicating with bank',
    finalizing: 'Finalizing payment',
    amount: 'Payment amount',
    card: 'Payment card',
    method: 'Payment method',
    methodCash: 'Cash',
    methodTransfer: 'Bank transfer',
    orderRegistering: 'Registering order',
    orderConfirming: 'Notifying the store',
  }
};

export function PaymentProcessingModal({
  isOpen,
  onError,
  cardInfo,
  amount,
  method = 'card',
}: PaymentProcessingModalProps) {
  const { language, currency } = useApp();
  const [currentStep, setCurrentStep] = useState(0);
  const t = translations[language];
  const isNonCard = method === 'cash' || method === 'transfer';

  const steps = isNonCard
    ? [t.orderRegistering, t.orderConfirming, t.finalizing]
    : [t.cardVerification, t.bankCommunication, t.finalizing];

  useEffect(() => {
    if (!isOpen) return;

    // 진행 단계 표시용 타이머 (UI 연출)
    const timer1 = setTimeout(() => setCurrentStep(1), 2000);
    const timer2 = setTimeout(() => setCurrentStep(2), 4000);

    // #687 fail-closed: 기존 Math.random 모의 결과(80% 성공)가 서버 확정 없이
    // paymentFlow 'success'(거짓 '결제 완료' 화면)에 도달하던 P1 root cause.
    // 클라이언트는 결제 성공을 판정할 수 없으므로 항상 오류 처리로 종료한다.
    const timer3 = setTimeout(() => {
      onError();
    }, 6000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isOpen, onError]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-auto shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-primary to-primary-hover rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.processing}</h2>
          <p className="text-gray-600">{t.pleaseWait}</p>
        </div>

        {/* Payment Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm text-gray-600">{t.amount}</span>
            <span className="font-semibold text-gray-900">{formatCurrency(amount, currency)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{isNonCard ? t.method : t.card}</span>
            {isNonCard ? (
              <span className="text-sm font-medium text-gray-900">
                {method === 'cash' ? t.methodCash : t.methodTransfer}
              </span>
            ) : cardInfo ? (
              <div className="flex items-center gap-2">
                <div className={`w-8 h-5 rounded flex items-center justify-center ${
                  cardInfo.type === 'visa' ? 'bg-blue-600' : 'bg-red-500'
                }`}>
                  <span className="text-xs font-bold text-white">
                    {cardInfo.type.toUpperCase()}
                  </span>
                </div>
                <span className="text-sm font-mono">•••• {cardInfo.lastFour}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Progress Steps */}
        <div className="space-y-4">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                index < currentStep
                  ? 'bg-primary text-white'
                  : index === currentStep
                    ? 'bg-primary/20 border-2 border-primary'
                    : 'bg-gray-200'
              }`}>
                {index < currentStep ? (
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : index === currentStep ? (
                  <Loader2 className="w-3 h-3 text-primary animate-spin" />
                ) : (
                  <span className="text-xs text-gray-400">{index + 1}</span>
                )}
              </div>
              <span className={`text-sm ${
                index <= currentStep ? 'text-gray-900 font-medium' : 'text-gray-400'
              }`}>
                {step}
              </span>
            </div>
          ))}
        </div>

        {/* Security Info */}
        <div className="mt-6 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-800">
            {language === 'ko' && '결제 정보는 SSL로 암호화되어 안전하게 처리됩니다.'}
            {language === 'es' && 'La información de pago se procesa de forma segura con cifrado SSL.'}
            {language === 'en' && 'Payment information is securely processed with SSL encryption.'}
          </p>
        </div>
      </div>
    </div>
  );
}